import { ApIcon, ApPopover } from '@alixpartners/ui-components';
import * as React from 'react';
import {
  IBenchmarkBreakdown,
  IIndustryCalculation,
  IBenchmarkCalculationType,
} from 'utils/types';

import {
  Bold,
  CalculationDescription,
  CalculationsTitle,
  CalculationTable,
  CalculationTableCell,
  CalculationTableErrorCell,
  CalculationTableColumn,
  CalculationTableHeaderCell,
  CalculationTableInfoCell,
  CalculationTableLeft,
  CalculationTableRight,
  Container,
  PopoverContainer,
  PopoverRow,
  PopoverTitle,
  Row,
} from './Benchmarks.styles';

const VALUES_PADDING = 132;
const IT_STAFF_BENCHMARK_NAME = 'IT Staff Mix - Average Percentage of IT Staff';
enum VALUE_TYPE_MAP {
  ValueType = 'Value type',
  JobRole = 'Job role',
}

const getCalculationValueParsed = (value: string) => {
  const firstPart = value.split('%(')[0];
  const percentageSearchResult = value.match(/\(([^)]+)\)/);
  const secondPart =
    percentageSearchResult && percentageSearchResult[1]
      ? percentageSearchResult[1].replace('%', '')
      : '0';
  return `${firstPart} x ${secondPart}`;
};

export const RowDescriptionHeader = ({
  type,
}: {
  type: IBenchmarkCalculationType;
}) => (
  <Row>
    <CalculationTableColumn width={180}>
      <CalculationTableHeaderCell>
        {VALUE_TYPE_MAP[type]}
      </CalculationTableHeaderCell>
    </CalculationTableColumn>
    <CalculationTableColumn width={72}>
      <CalculationTableHeaderCell>Year</CalculationTableHeaderCell>
    </CalculationTableColumn>
    <CalculationTableColumn width={108}>
      <CalculationTableHeaderCell>Blend value</CalculationTableHeaderCell>
    </CalculationTableColumn>
  </Row>
);

export const RowDescription = ({
  industryCalculations,
  valueType,
  year,
  blendValue,
  isHovered,
  isMetricUpliftDisabled,
  onMouseOver,
  onMouseOut,
  revenueUpliftPercentage,
}: {
  valueType: string;
  year: number;
  blendValue: string;
  isHovered?: boolean;
  isMetricUpliftDisabled: boolean;
  revenueUpliftPercentage?: number;
  industryCalculations: IIndustryCalculation[];
  onMouseOver: () => void;
  onMouseOut: () => void;
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [isOpen, setOpen] = React.useState(false);
  const toggleOpen = React.useCallback(
    () => setOpen(!isOpen),
    [setOpen, isOpen]
  );

  return (
    <Row
      highlighted={isHovered}
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}
    >
      <CalculationTableColumn width={180}>
        <CalculationTableCell>{valueType}</CalculationTableCell>
      </CalculationTableColumn>
      <CalculationTableColumn width={72}>
        <CalculationTableCell>{year}</CalculationTableCell>
      </CalculationTableColumn>
      <CalculationTableColumn width={108}>
        <CalculationTableCell>{blendValue}</CalculationTableCell>
      </CalculationTableColumn>

      <CalculationTableColumn width={32}>
        <CalculationTableInfoCell
          ref={ref}
          onMouseOver={() => setOpen(true)}
          onMouseOut={() => setOpen(false)}
        >
          <ApIcon iconName="info" iconSize="20px" />

          {isOpen && (
            <ApPopover
              referenceElement={ref.current}
              onClose={toggleOpen}
              placement="right"
            >
              <PopoverContainer>
                <PopoverTitle>Calculations</PopoverTitle>
                {industryCalculations.map((calculation, index) => (
                  <>
                    <PopoverRow>
                      <Bold>
                        {getCalculationValueParsed(calculation.value)}
                      </Bold>{' '}
                      ({calculation.providerName})
                    </PopoverRow>
                    {!(
                      index === industryCalculations.length - 1 &&
                      isMetricUpliftDisabled
                    ) ? (
                      <PopoverRow>+</PopoverRow>
                    ) : null}
                  </>
                ))}
                {!isMetricUpliftDisabled && (
                  <PopoverRow>
                    <Bold>{revenueUpliftPercentage}%</Bold> Metric uplift
                  </PopoverRow>
                )}
              </PopoverContainer>
            </ApPopover>
          )}
        </CalculationTableInfoCell>
      </CalculationTableColumn>
    </Row>
  );
};

export const RowValuesHeader = ({ values }: { values: string[] }) => (
  <Row>
    {values.map((value, index) => (
      <CalculationTableColumn
        key={`${value}-${index}`}
        padding={index === 0 ? VALUES_PADDING : 0}
        width={180 + (index === 0 ? VALUES_PADDING : 0)}
      >
        <CalculationTableHeaderCell title={value}>
          {value}
        </CalculationTableHeaderCell>
      </CalculationTableColumn>
    ))}
  </Row>
);

export const RowValues = ({
  isHovered,
  onMouseOver,
  onMouseOut,
  values,
}: {
  isHovered?: boolean;
  onMouseOver: () => void;
  onMouseOut: () => void;
  values: IIndustryCalculation[];
}) => (
  <Row
    highlighted={isHovered}
    onMouseOver={onMouseOver}
    onMouseOut={onMouseOut}
  >
    {values.map(({ value }, index) => (
      <CalculationTableColumn
        key={`${value}-${index}`}
        padding={index === 0 ? VALUES_PADDING : 0}
        width={180 + (index === 0 ? VALUES_PADDING : 0)}
      >
        {value === '-' ? (
          <CalculationTableErrorCell>
            Benchmark is not available
          </CalculationTableErrorCell>
        ) : (
          <CalculationTableCell>{value}</CalculationTableCell>
        )}
      </CalculationTableColumn>
    ))}
  </Row>
);

interface ICalculationsProps extends IBenchmarkBreakdown {
  revenueUpliftPercentage?: number;
}

export const Calculations = ({
  providerName,
  benchmarkName,
  benchmarkCalculations,
  revenueUpliftPercentage,
}: ICalculationsProps) => {
  const [rowHoveredIndex, setRowHoveredIndex] = React.useState<number | null>(
    null
  );
  return (
    <Container>
      <CalculationsTitle>{benchmarkName}</CalculationsTitle>
      <CalculationDescription>{providerName}</CalculationDescription>
      <CalculationTable>
        <CalculationTableLeft>
          <RowDescriptionHeader type={benchmarkCalculations[0].type} />
          <>
            {benchmarkCalculations.map((row, index) => (
              <RowDescription
                key={`${row.value}-${row.blendValue}-${index}`}
                valueType={row.value}
                year={row.year}
                blendValue={row.blendValue}
                isHovered={rowHoveredIndex === index}
                isMetricUpliftDisabled={
                  benchmarkName === IT_STAFF_BENCHMARK_NAME
                }
                revenueUpliftPercentage={revenueUpliftPercentage}
                industryCalculations={row.industryCalculations}
                onMouseOver={() => setRowHoveredIndex(index)}
                onMouseOut={() => setRowHoveredIndex(null)}
              />
            ))}
          </>
        </CalculationTableLeft>
        <CalculationTableRight>
          <RowValuesHeader
            values={benchmarkCalculations[0].industryCalculations.map(
              (industry) => industry.providerName
            )}
          />
          {benchmarkCalculations.map((row, index) => (
            <RowValues
              key={`${row.value}-${row.blendValue}-${index}`}
              isHovered={rowHoveredIndex === index}
              onMouseOver={() => setRowHoveredIndex(index)}
              onMouseOut={() => setRowHoveredIndex(null)}
              values={row.industryCalculations}
            />
          ))}
        </CalculationTableRight>
      </CalculationTable>
    </Container>
  );
};
